<script setup>
import { computed } from 'vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import {
  maskEmail,
  maskPhoneNumber,
  createEncryptedContactLink,
  handleEncryptedContactClick,
} from 'shared/helpers/contactPrivacyHelper';

const props = defineProps({
  id: {
    type: [String, Number],
    default: 0,
  },
  email: {
    type: String,
    default: '',
  },
  phone: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: '',
  },
  thumbnail: {
    type: String,
    default: '',
  },
  accountId: {
    type: [String, Number],
    default: 0,
  },
  customAttributes: {
    type: Object,
    default: () => ({}),
  },
  additionalAttributes: {
    type: Object,
    default: () => ({}),
  },
});

const navigateTo = computed(() => {
  return frontendURL(`accounts/${props.accountId}/contacts/${props.id}`);
});

const maskedEmail = computed(() => (props.email ? maskEmail(props.email) : ''));
const maskedPhone = computed(() =>
  props.phone ? maskPhoneNumber(props.phone) : ''
);

const encryptedEmailLink = computed(() =>
  props.email
    ? createEncryptedContactLink('email', props.email, {
        custom_attributes: props.customAttributes,
        additional_attributes: props.additionalAttributes,
      })
    : ''
);
const encryptedPhoneLink = computed(() =>
  props.phone
    ? createEncryptedContactLink('phone', props.phone, {
        custom_attributes: props.customAttributes,
        additional_attributes: props.additionalAttributes,
      })
    : ''
);

const handleEmailClick = e => {
  e.preventDefault();
  e.stopPropagation();
  if (encryptedEmailLink.value) {
    handleEncryptedContactClick(encryptedEmailLink.value);
  }
};

const handlePhoneClick = e => {
  e.preventDefault();
  e.stopPropagation();
  if (encryptedPhoneLink.value) {
    handleEncryptedContactClick(encryptedPhoneLink.value);
  }
};
</script>

<template>
  <router-link
    :to="navigateTo"
    class="flex items-start p-2 rounded-xl cursor-pointer hover:bg-n-slate-2"
  >
    <Avatar
      :name="name"
      :src="thumbnail"
      :size="24"
      rounded-full
      class="mt-0.5"
    />
    <div class="ml-2 rtl:mr-2 min-w-0 rtl:ml-0">
      <h5 class="text-sm name truncate min-w-0 text-n-slate-12">
        {{ name }}
      </h5>
      <p
        class="grid items-center m-0 gap-1 text-sm grid-cols-[minmax(0,1fr)_auto_auto]"
      >
        <a
          v-if="email"
          href="#"
          class="truncate text-n-slate-12 hover:underline cursor-pointer"
          :data-encrypted-link="encryptedEmailLink"
          :title="maskedEmail"
          @click="handleEmailClick"
        >
          {{ maskedEmail }}
        </a>
        <span v-if="phone" class="text-n-slate-10">•</span>
        <a
          v-if="phone"
          href="#"
          class="text-n-slate-12 hover:underline cursor-pointer"
          :data-encrypted-link="encryptedPhoneLink"
          @click="handlePhoneClick"
        >
          {{ maskedPhone }}
        </a>
      </p>
    </div>
  </router-link>
</template>
